import BaseListPresenter from "../../../base/BaseListPresenter";
import { Button, dialog } from "nq-component";
import { findObjectUseCase, upsertUseCase } from "../../../usecases/object";
import { ServiceRecordLeave } from "../../../components/common/Converter";
import {
  buildHierarchyWhere,
  findCurrentUserEmployee,
} from "../../../utils/hierarchy";

class EmployeePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  componentDidMount() {
    this.init();
    return this.getObjects();
  }

  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = [
      "position",
      "position.salaryPointer",
      "employmentType",
      "nonRegPosition",
      "selectedSchedule",
    ];
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  reset() {
    const getCurrentPage = localStorage.getItem("currentPage") || 1;
    this.objects = [];
    this.count = 0;
    this.current = Number(getCurrentPage) || 1;
    this.view.setObjects([]);
    this.view.setCount(0);
    this.view.setTotal(0);
  }

  async getObjects() {
    this.reset();
    await this.countObjects();
    await this.findObjects();
  }

  getIsScheduler() {
    const currentUser = this.view.getCurrentUser();
    const roles = currentUser?.roles || [];
    return roles.some((item) =>
      ["supervisor", "scheduler"].includes(item?.id?.toLowerCase()),
    );
  }

  async countObjects() {
    try {
      this.showProgress();
      const collection = this.view.getCollectionName();
      const employees = await findCurrentUserEmployee(
        findObjectUseCase(),
        this.view.getCurrentUser(),
      );
      const isScheduler = this.getIsScheduler();
      const query = this.createQuery(employees, isScheduler);

      let { count } = await this.countObjectUseCase.execute(collection, {
        where: query.where,
      });

      // ✅ Fixed: subtract separated employees from the count so pagination is accurate
      if (isScheduler) {
        const separated = await findObjectUseCase().execute("seperated", {
          keys: ["employee"],
        });
        count = Math.max(0, count - separated.length);
      }

      this.count = count;
      this.view.setCount(this.count);
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
  }

  createQuery(employee, isScheduler) {
    this.where = {};

    if (isScheduler && Array.isArray(employee) && employee.length > 0) {
      this.where = buildHierarchyWhere(employee[0]);
    }

    console.log("WHERE: ", this.where);

    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const employees = await findCurrentUserEmployee(
      findObjectUseCase(),
      this.view.getCurrentUser(),
    );
    const isScheduler = this.getIsScheduler();
    const query = this.createQuery(employees, isScheduler);

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);

      let filteredObjects = objects;
      if (isScheduler) {
        const separated = await findObjectUseCase().execute("seperated", {
          keys: ["employee"],
        });
        const separatedIds = new Set(separated.map((s) => s.employee?.id));
        filteredObjects = objects.filter((obj) => !separatedIds.has(obj.id));
      }

      filteredObjects.forEach((obj) => {
        if (obj.employmentType?.types === "Plantilla") {
          obj.positionLabel = obj.position?.position_title || "";
        } else if (obj.employmentType?.types === "Non-Plantilla") {
          obj.positionLabel = obj.nonRegPosition?.positionTitle || "";
        }
      });

      this.objects = filteredObjects;
      this.view.setObjects(filteredObjects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  onSelect(index) {
    const selectedObjects = this.view.state.selected;
    const selected = this.objects[index];
    const i = selectedObjects.findIndex((item) => item.id === selected.id);
    if (i > -1) {
      selectedObjects.splice(i, 1);
    } else {
      selectedObjects.push(selected);
    }
    this.view.setState({ selected: selectedObjects });
  }

  async handleSubmitUnSuspension(data) {
    data.suspension = false;
    data.dateOfSuspension = "";
    const { employmentType, ...resProps } = data;
    const employeeData = { ...resProps };
    try {
      await upsertUseCase().execute("employees", employeeData);
      this.insertServiceRecord(data, "Unsuspension");
      dialog.close();
      this.view.refetchData();
    } catch (e) {
      console.error("Error: ", e);
    }
  }

  async insertServiceRecord(updateEmployee, reason) {
    const effectivity = new Date();
    const serviceRec = await findObjectUseCase().execute("service_record", {
      where: { empID: updateEmployee?.id, to: { $exists: false } },
    });

    const dateFrom = serviceRec[0]?.from
      ? new Date(serviceRec[0]?.from)
      : new Date();
    dateFrom.setDate(dateFrom.getDate() - 1);

    const leaveReq = await findObjectUseCase().execute("leave_request", {
      where: {
        employee: { id: updateEmployee.id },
        stats: { $ne: "For Processing" },
        $or: [
          { startdate: { $gte: dateFrom, $lte: effectivity } },
          { enddate: { $gte: dateFrom, $lte: effectivity } },
        ],
      },
    });

    let salary = "";
    if (updateEmployee?.salaryGrade) {
      salary = updateEmployee?.salaryGrade.replace(
        /₱\s?([\d,]+\.\d{2})/,
        (_, num) => `₱ ${(parseFloat(num.replace(/,/g, "")) * 12).toFixed(2)}`,
      );
    }

    const serviceLog = {
      email: updateEmployee?.email,
      empID: updateEmployee?.id,
      from: effectivity,
      employee: { id: updateEmployee.id },
      designation: updateEmployee.position.position_title,
      status: updateEmployee?.employmentType?.name || "",
      salary: salary,
      station: "BUREAU OF IMMIGRATION",
      branch: "NATIONAL",
      cause: reason,
    };

    if (serviceRec.length > 0) {
      let leaveStatus = [];
      for (const leave of leaveReq) {
        const selectedType = leave.selectedType;
        const abbreviation = selectedType
          .split(" ")
          .map((word) => word.charAt(0))
          .join("");
        leaveStatus.push(
          ServiceRecordLeave(
            abbreviation,
            new Date(leave?.startdate),
            new Date(leave?.enddate),
          ),
        );
      }
      serviceRec[0].to = serviceLog?.from;
      serviceRec[0].leave = leaveStatus;
      serviceRec[0].status = serviceLog?.status;
      await upsertUseCase().execute("service_record", serviceRec[0]);
    }
    await upsertUseCase().execute("service_record", serviceLog);
  }

  async handleUnSuspension(data) {
    dialog.fire({
      html: (
        <div className="p-2">
          <div className="row">
            <h4>Suspension Confirmation</h4>
            <hr />
            <p style={{ fontSize: "13px", padding: "1rem" }}>
              Are you sure you want to lift the suspension of this employee?
            </p>
            <hr />
          </div>
          <div className="w-100 d-flex justify-content-end gap-4 pe-3 pb-3">
            <Button
              className="btn btn-light fs-sm"
              onClick={() => dialog.close()}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={() => this.handleSubmitUnSuspension(data)}
            >
              Confirm
            </Button>
          </div>
        </div>
      ),
      footer: false,
    });
  }
}

export default EmployeePresenter;
